(() => {
  if (customElements.get('voler-chamois-guide')) return;

  customElements.define('voler-chamois-guide', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.currentFit = 'men';
      this.currentPick = 'elan';
      this.picks = {
        elan: {
          name: 'Elan',
          ride: '2–3 hours',
          foam: '3 densities · 20–70 kg/m³',
          thickness: '3–14 mm',
          images: { men: this.dataset.elanMenImage, women: this.dataset.elanWomenImage }
        },
        paris: {
          name: 'Paris',
          ride: '6+ hours',
          foam: '3 densities · 80–120 kg/m³',
          thickness: '3–13 mm',
          images: { men: this.dataset.parisImage, women: this.dataset.parisImage }
        },
        comphp: {
          name: 'Comp HP',
          ride: '6+ hours',
          foam: '4 densities · 60–120 kg/m³',
          thickness: '3–11 mm',
          images: { men: this.dataset.comphpMenImage, women: this.dataset.comphpWomenImage }
        },
        fusion: {
          name: 'Fusion',
          ride: 'Long-course tri',
          foam: 'Dual density',
          thickness: 'Low profile',
          images: { men: this.dataset.fusionImage, women: this.dataset.fusionImage }
        }
      };
      this.specialtyPads = {
        fusion: {
          tag: 'Triathlon',
          name: 'Fusion',
          image: this.dataset.fusionImage,
          alt: 'Fusion triathlon chamois pad',
          width: 1152,
          height: 1536,
          description: 'Low-profile, dual-density foam for Ironman and long-course time trials. A perforated foam core improves airflow, while the Microline nylon surface wicks moisture and stretches in every direction.',
          facts: [['Profile', 'Low'], ['Foam', 'Dual density'], ['Use', 'Long-course triathlon']]
        },
        gravel: {
          tag: 'Unisex',
          name: 'Gravel',
          image: this.dataset.gravelImage,
          alt: 'Unisex Gravel chamois pad',
          width: 1145,
          height: 1374,
          description: 'Purpose-built for 3–4 hour gravel rides, with multi-density foam and the same silver antibacterial face fabric used on Elan.',
          facts: [['Ride time', '3–4 hours'], ['Foam', 'Multi-density'], ['Face fabric', 'Silver antibacterial']]
        }
      };

      this.resultFields = {
        name: this.querySelector('[data-chamois-result="name"]'),
        ride: this.querySelector('[data-chamois-result="ride"]'),
        foam: this.querySelector('[data-chamois-result="foam"]'),
        thickness: this.querySelector('[data-chamois-result="thickness"]'),
        image: this.querySelector('[data-chamois-result-image]')
      };
      this.specialtyFields = {
        panel: this.querySelector('[role="tabpanel"]'),
        image: this.querySelector('[data-chamois-specialty-image]'),
        tag: this.querySelector('[data-chamois-specialty-tag]'),
        title: this.querySelector('[data-chamois-specialty-title]'),
        description: this.querySelector('[data-chamois-specialty-description]'),
        facts: this.querySelector('[data-chamois-specialty-facts]')
      };

      this.bindRideOptions();
      this.bindShopLinks();
      this.bindComparison();
      this.bindFitOptions();
      this.bindSpecialtyOptions();
      this.renderPick(this.currentPick);
      this.renderSpecialty('fusion');
    }

    bindRideOptions() {
      this.querySelectorAll('[data-chamois-pick]').forEach((button) => {
        button.addEventListener('click', () => this.selectPick(button.dataset.chamoisPick));
      });
    }

    selectPick(key) {
      if (!this.picks[key]) return;
      this.currentPick = key;
      this.querySelectorAll('[data-chamois-pick]').forEach((item) => {
        const selected = item.dataset.chamoisPick === key;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', String(selected));
      });
      this.renderPick(key);
    }

    bindShopLinks() {
      this.querySelectorAll('[data-chamois-shop]').forEach((link) => {
        link.addEventListener('click', (event) => {
          const key = link.dataset.chamoisShop;
          const rail = Array.from(this.querySelectorAll('[data-chamois-product-rail]'))
            .find((item) => item.dataset.chamoisProductRail === key);
          if (!this.picks[key] || !rail) return;
          event.preventDefault();
          this.selectPick(key);
          const heading = rail.querySelector('.voler-chamois-guide__product-rail-heading h3');
          if (heading) {
            heading.scrollIntoView({ block: 'start' });
            heading.focus({ preventScroll: true });
          }
        });
      });
    }

    bindComparison() {
      const details = this.querySelector('[data-chamois-comparison]');
      const link = this.querySelector('[data-chamois-compare-link]');
      if (!details) return;
      const mobile = window.matchMedia('(max-width: 699px)');
      const comparisonId = details.closest('section')?.id;
      const setForViewport = () => {
        details.open = !mobile.matches || window.location.hash === `#${comparisonId}`;
        details.dataset.ready = 'true';
      };
      setForViewport();
      mobile.addEventListener('change', setForViewport);
      window.addEventListener('hashchange', () => {
        if (mobile.matches && window.location.hash === `#${comparisonId}`) details.open = true;
      });
      link?.addEventListener('click', () => {
        if (!mobile.matches) return;
        details.open = true;
        requestAnimationFrame(() => details.querySelector('summary')?.focus({ preventScroll: true }));
      });
    }

    bindFitOptions() {
      this.querySelectorAll('[data-chamois-fit]').forEach((button) => {
        button.addEventListener('click', () => {
          this.currentFit = button.dataset.chamoisFit;
          this.querySelectorAll('[data-chamois-fit]').forEach((item) => {
            const selected = item === button;
            item.classList.toggle('is-selected', selected);
            item.setAttribute('aria-pressed', String(selected));
          });
          this.querySelectorAll('[data-men-image][data-women-image]').forEach((image) => {
            const card = image.closest('[data-chamois-card]');
            const padName = card.querySelector('h3').textContent.trim();
            image.src = this.currentFit === 'women' ? image.dataset.womenImage : image.dataset.menImage;
            image.alt = `${this.currentFit === 'men' ? "Men's" : "Women's"} ${padName} chamois pad`;
          });
          this.renderPick(this.currentPick);
        });
      });
    }

    bindSpecialtyOptions() {
      const tabs = Array.from(this.querySelectorAll('[data-chamois-specialty]'));
      const select = (button, moveFocus = false) => {
        tabs.forEach((item) => {
          const selected = item === button;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
        });
        this.specialtyFields.panel.setAttribute('aria-labelledby', button.id);
        this.renderSpecialty(button.dataset.chamoisSpecialty);
        if (moveFocus) button.focus();
      };

      tabs.forEach((button, index) => {
        button.addEventListener('click', () => select(button));
        button.addEventListener('keydown', (event) => {
          let targetIndex;
          if (event.key === 'ArrowRight') targetIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') targetIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') targetIndex = 0;
          if (event.key === 'End') targetIndex = tabs.length - 1;
          if (targetIndex === undefined) return;
          event.preventDefault();
          select(tabs[targetIndex], true);
        });
      });
    }

    renderPick(key) {
      const pick = this.picks[key];
      const gendered = key === 'elan' || key === 'comphp';
      this.resultFields.name.textContent = pick.name;
      this.resultFields.ride.textContent = pick.ride;
      this.resultFields.foam.textContent = pick.foam;
      this.resultFields.thickness.textContent = pick.thickness;
      this.resultFields.image.src = pick.images[this.currentFit];
      this.resultFields.image.alt = gendered
        ? `${this.currentFit === 'men' ? "Men's" : "Women's"} ${pick.name} chamois pad`
        : key === 'fusion'
          ? 'Fusion triathlon chamois pad'
          : `${pick.name} chamois pad`;

      this.querySelectorAll('[data-chamois-card]').forEach((card) => {
        card.classList.toggle('is-recommended', card.dataset.chamoisCard === key);
      });
      this.querySelectorAll('[data-chamois-product-rail]').forEach((rail) => {
        const selected = rail.dataset.chamoisProductRail === key;
        rail.hidden = !selected;
        if (selected) {
          const carousel = rail.querySelector('scroll-carousel');
          if (carousel) carousel.scrollLeft = 0;
        }
      });
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    }

    renderSpecialty(key) {
      const pad = this.specialtyPads[key];
      this.specialtyFields.image.src = pad.image;
      this.specialtyFields.image.alt = pad.alt;
      this.specialtyFields.image.width = pad.width;
      this.specialtyFields.image.height = pad.height;
      this.specialtyFields.tag.textContent = pad.tag;
      this.specialtyFields.title.textContent = pad.name;
      this.specialtyFields.description.textContent = pad.description;
      this.specialtyFields.facts.replaceChildren(...pad.facts.map(([label, value]) => {
        const item = document.createElement('div');
        const term = document.createElement('dt');
        const description = document.createElement('dd');
        term.textContent = label;
        description.textContent = value;
        item.append(term, description);
        return item;
      }));
    }
  });
})();
